/**
 * AddPatientPaymentMethodModal Component
 *
 * Modal dialog with Stripe Elements for adding a payment method for a patient.
 * Uses shared StripePaymentForm to support cards and ACH with billing details.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { AccountBalance, CheckCircle, Schedule } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';

import {
  StripePaymentForm,
  type StripePaymentFormHandle,
} from '@features/subscriptions/components/StripePaymentForm';

import { usePatientPaymentMethods, useStripeConnect } from '../store/hooks';

// Cache for Stripe instances by account ID
const stripeInstanceCache: Record<string, Promise<Stripe | null>> = {};

/**
 * Get or create a Stripe instance for the given connected account
 */
function getStripeInstance(stripeAccountId: string | null): Promise<Stripe | null> {
  const cacheKey = stripeAccountId || 'platform';

  if (!stripeInstanceCache[cacheKey]) {
    const publishableKey = (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string) || '';
    stripeInstanceCache[cacheKey] = loadStripe(
      publishableKey,
      stripeAccountId ? { stripeAccount: stripeAccountId } : undefined
    );
  }

  return stripeInstanceCache[cacheKey];
}

/**
 * Verification pending state shown when micro-deposits are required
 */
function VerificationPendingContent({ onClose }: { onClose: () => void }) {
  return (
    <>
      <DialogContent>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Schedule sx={{ fontSize: 64, color: 'info.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Bank Account Verification Pending
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            We need to verify the bank account before it can be used for payments.
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            What happens next:
          </Typography>
          <List dense disablePadding>
            <ListItem disableGutters sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircle sx={{ fontSize: 18, color: 'info.main' }} />
              </ListItemIcon>
              <ListItemText
                primary="Two small deposits will be sent to the bank account"
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            <ListItem disableGutters sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircle sx={{ fontSize: 18, color: 'info.main' }} />
              </ListItemIcon>
              <ListItemText
                primary="This usually takes 1-2 business days"
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            <ListItem disableGutters sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CheckCircle sx={{ fontSize: 18, color: 'info.main' }} />
              </ListItemIcon>
              <ListItemText
                primary="The patient will receive verification instructions via email"
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
          </List>
        </Alert>

        <Typography variant="body2" color="text.secondary">
          Once verified, the bank account will be automatically saved as a payment method.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={onClose}>
          Got It
        </Button>
      </DialogActions>
    </>
  );
}

interface AddPaymentMethodFormWrapperProps {
  patientId: string;
  onSuccess: () => void;
  onCancel: () => void;
  onVerificationPending: () => void;
}

/**
 * Form wrapper that uses the shared StripePaymentForm
 */
function AddPaymentMethodFormWrapper({
  patientId,
  onSuccess,
  onCancel,
  onVerificationPending,
}: AddPaymentMethodFormWrapperProps) {
  const formRef = useRef<StripePaymentFormHandle>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { confirmNewPaymentMethod, isConfirming } = usePatientPaymentMethods(patientId);

  const handleSubmit = async () => {
    if (!formRef.current) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Confirm payment with Stripe via shared form
      await formRef.current.confirmPayment();

      // Confirm the payment method on our backend
      await confirmNewPaymentMethod();

      onSuccess();
    } catch (err) {
      // Error is already displayed by StripePaymentForm
      // Only set local error if it's from our API call
      if (err instanceof Error && err.message.includes('confirm')) {
        setError(err.message);
      }
      setIsSubmitting(false);
    }
  };

  const isProcessing = isSubmitting || isConfirming;

  return (
    <>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Add a payment method for this patient. You can choose between credit card or bank account
          (ACH).
        </Typography>

        <StripePaymentForm
          ref={formRef}
          onVerificationPending={onVerificationPending}
          returnUrl={window.location.href}
          showProcessingIndicator={false}
        />

        {/* API Error Message */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={isProcessing}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isProcessing}
          startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isProcessing ? 'Saving...' : 'Save Payment Method'}
        </Button>
      </DialogActions>
    </>
  );
}

export interface AddPatientPaymentMethodModalProps {
  open: boolean;
  patientId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Modal wrapper that fetches SetupIntent and provides Stripe context
 */
export function AddPatientPaymentMethodModal({
  open,
  patientId,
  onClose,
  onSuccess,
}: AddPatientPaymentMethodModalProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [verificationPending, setVerificationPending] = useState(false);
  const [intentError, setIntentError] = useState<Error | null>(null);
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);

  const { setupNewPaymentMethod } = usePatientPaymentMethods(patientId);
  const { stripeAccountId } = useStripeConnect();

  // Get Stripe instance for the connected account
  const stripePromise = useMemo(
    () => getStripeInstance(stripeAccountId),
    [stripeAccountId]
  );

  // Fetch SetupIntent when modal opens
  useEffect(() => {
    if (open && !clientSecret && !verificationPending) {
      setIsCreatingIntent(true);
      setIntentError(null);

      setupNewPaymentMethod()
        .then(data => {
          if (data.client_secret) {
            setClientSecret(data.client_secret);
          }
        })
        .catch(err => {
          setIntentError(err);
        })
        .finally(() => {
          setIsCreatingIntent(false);
        });
    }
  }, [open, clientSecret, verificationPending, setupNewPaymentMethod]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setClientSecret(null);
      setVerificationPending(false);
      setIntentError(null);
    }
  }, [open]);

  const handleSuccess = () => {
    onSuccess?.();
    onClose();
  };

  const handleVerificationPending = () => {
    setVerificationPending(true);
  };

  const handleRetry = () => {
    setClientSecret(null);
    setIntentError(null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccountBalance color="primary" />
        {verificationPending ? 'Verification Required' : 'Add Payment Method'}
      </DialogTitle>

      {/* Verification pending state */}
      {verificationPending && <VerificationPendingContent onClose={onClose} />}

      {/* Loading state while fetching SetupIntent */}
      {!verificationPending && isCreatingIntent && (
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4, gap: 2 }}>
            <CircularProgress size={24} />
            <Typography color="text.secondary">Preparing secure form...</Typography>
          </Box>
        </DialogContent>
      )}

      {/* Error state */}
      {!verificationPending && intentError && (
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to initialize payment form. Please try again.
          </Alert>
          <DialogActions sx={{ px: 0, pb: 0 }}>
            <Button onClick={onClose}>Close</Button>
            <Button variant="contained" onClick={handleRetry}>
              Retry
            </Button>
          </DialogActions>
        </DialogContent>
      )}

      {/* Payment form once we have the client secret */}
      {!verificationPending && open && clientSecret && !intentError && (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: {
              theme: 'stripe',
              variables: {
                colorPrimary: '#1976d2',
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              },
            },
          }}
        >
          <AddPaymentMethodFormWrapper
            patientId={patientId}
            onSuccess={handleSuccess}
            onCancel={onClose}
            onVerificationPending={handleVerificationPending}
          />
        </Elements>
      )}
    </Dialog>
  );
}

export default AddPatientPaymentMethodModal;
